import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import classnames from "classnames";
import ChevronLeft from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/ChevronLeft24PxOutlined";
import ChevronRight from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/ChevronRight24PxOutlined";
import { GalleryVideoElement } from "../../../components/GalleryVideoElement/GalleryVideoElement";
import styles from "./Carousel.module.scss";
import { Thumbnails } from "../Thumbnails/Thumbnails";
import { ImageElement } from "../../../components/ZoomModal/ModalContent/ImageElement";
import { dispatchGalleryMonetateQEvent } from "../../../components/Analytics/dispatchGalleryMonetateQEvent";
import { imageProfileGenerator } from "../../../../../utils";
import { useRouter } from "next/router";

export type MediaItem =
  | {
      type: "video";
      imageUrl: string;
      thumbnailUrl: string;
      videoUrl: string;
      posterUrl: string;
      title: string;
    }
  | {
      type: "image";
      imageUrl: string;
      thumbnailUrl: string;
      title: string;
    };

type CarouselItemProps = {
  mediaItem: MediaItem;
  containerEl: HTMLElement | null;
  setSelected: (arg: number) => void;
  index: number;
  fullscreen?: boolean;
  imageSizes?: string;
};

type CarouselProps = {
  mediaItems: MediaItem[];
  selectedInd: number;
  setSelectedInd: (arg: number) => void;
  fullscreen?: boolean;
  imageSizes?: string;
  hasImmediateStateUpdates?: boolean;
};

function scrollIntoView(container: HTMLElement, target: HTMLElement) {
  const targetRect = target.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const targetScrollXPos =
    target.offsetLeft - (containerRect.width - targetRect.width) / 2;
  const targetScrollYPos =
    target.offsetTop - (containerRect.height - targetRect.height) / 2;

  container.scrollTo(targetScrollXPos, targetScrollYPos);
}

const CarouselItem = ({
  mediaItem,
  containerEl,
  setSelected,
  index,
  fullscreen,
  imageSizes,
}: CarouselItemProps) => {
  const { ref, inView } = useInView({
    root: containerEl,
    threshold: 0.5,
  });

  useEffect(() => {
    if (inView) {
      setSelected(index);
    }
  }, [inView]);

  if (!mediaItem) {
    return null;
  }

  return mediaItem.type === "image" ? (
    <div ref={ref} key={mediaItem.imageUrl} className={styles.galleryItem}>
      {fullscreen ? (
        <ImageElement
          image={mediaItem}
          key={mediaItem.imageUrl}
          setModalHasInteraction={() => {}}
          isSelectedIndex={inView}
        />
      ) : (
        <img
          src={mediaItem.imageUrl}
          alt=""
          data-testid={`carousel-image-${index}`}
          className={styles.galleryImage}
          sizes={imageSizes}
          srcSet={imageProfileGenerator(mediaItem.imageUrl)}
          loading={index > 0 ? "lazy" : "eager"}
        />
      )}
    </div>
  ) : (
    <div ref={ref} key={mediaItem.imageUrl} className={styles.galleryItem}>
      <GalleryVideoElement
        className={styles.galleryImage}
        key={`galleryobjectkey-${index}`}
        video={mediaItem}
        videoId={index.toString()}
        isInView={inView}
      />
    </div>
  );
};

const generateMediaItemsKey = (mediaItems: MediaItem[]) =>
  mediaItems.map((item) => item.thumbnailUrl).join("|");

export const Carousel = ({
  mediaItems,
  selectedInd,
  setSelectedInd,
  children,
  fullscreen,
  imageSizes = "100vw",
  hasImmediateStateUpdates,
}: PropsWithChildren<CarouselProps>) => {
  const router = useRouter();
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const thumbsContainerRef = useRef<HTMLDivElement>(null);
  const [fullscreenTransitionEnded, setFullscreenTransitionEnded] =
    useState(false);
  const mediaItemRef = useRef(generateMediaItemsKey(mediaItems));
  const [loading, setLoading] = useState(false);

  const scrollToIndex = (index: number) => {
    if (index < 0 || index >= mediaItems.length || !mainContainerRef.current) {
      return;
    }

    scrollIntoView(
      mainContainerRef.current,
      mainContainerRef.current.children[index] as HTMLElement,
    );
  };

  const handleFullscreenTransitionEnd = () => {
    setFullscreenTransitionEnded(true);
  };

  useEffect(() => {
    if (fullscreen) {
      scrollToIndex(selectedInd);

      document
        .querySelector(".ReactModalPortal > div")
        ?.addEventListener("transitionend", handleFullscreenTransitionEnd);
    }

    return () => {
      document
        .querySelector(".ReactModalPortal > div")
        ?.removeEventListener("transitionend", handleFullscreenTransitionEnd);
    };
  }, []);

  useEffect(() => {
    if (thumbsContainerRef.current) {
      scrollIntoView(
        thumbsContainerRef.current,
        thumbsContainerRef.current.children[selectedInd] as HTMLElement,
      );
    }
  }, [selectedInd]);

  useEffect(() => {
    if (generateMediaItemsKey(mediaItems) !== mediaItemRef.current) {
      mainContainerRef.current?.scrollTo(0, 0);
      mediaItemRef.current = generateMediaItemsKey(mediaItems);
    }
  }, [mediaItems]);

  useEffect(() => {
    const handleRouteChange = () => {
      setLoading(true);
    };

    const handleRouteChangeComplete = () => {
      setLoading(false);
    };

    router.events.on("routeChangeStart", handleRouteChange);
    router.events.on("routeChangeComplete", handleRouteChangeComplete);

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
  }, [router]);

  const handlePrevClick = () => {
    scrollToIndex(selectedInd - 1);
    if (selectedInd > 0 && hasImmediateStateUpdates) {
      setSelectedInd(selectedInd - 1);
    }

    dispatchGalleryMonetateQEvent(
      "LeftArrow",
      fullscreen ? "imageModal" : "generic",
    );
  };

  const handleNextClick = () => {
    scrollToIndex(selectedInd + 1);
    if (selectedInd < mediaItems.length - 1 && hasImmediateStateUpdates) {
      setSelectedInd(selectedInd + 1);
    }
    dispatchGalleryMonetateQEvent(
      "RightArrow",
      fullscreen ? "imageModal" : "generic",
    );
  };

  return (
    <div
      id="image-gallery"
      className={classnames(styles.wrapper, {
        [styles["wrapper--fullscreen"]]: fullscreen,
      })}
      data-testid={fullscreen ? "gallery:zoom:modal" : "slot:header"}
    >
      <div
        className={classnames(styles.main, {
          [styles["main--fullscreen"]]: fullscreen,
          [styles["main--singleItem"]]: mediaItems.length < 2,
        })}
      >
        <div
          className={classnames(styles.gallery, {
            [styles["gallery--loading"]]:
              (fullscreen && !fullscreenTransitionEnded) || loading,
          })}
          ref={mainContainerRef}
        >
          {mediaItems.map((mediaItem, ind) => (
            <CarouselItem
              mediaItem={mediaItem}
              fullscreen={fullscreen}
              containerEl={mainContainerRef.current}
              setSelected={setSelectedInd}
              index={ind}
              key={`${mediaItem.imageUrl}-${ind}`}
              imageSizes={imageSizes}
            />
          ))}
        </div>
        <button
          className={styles.buttonPrev}
          type="button"
          onClick={handlePrevClick}
          data-testid="standard:gallery:navigation:left"
          disabled={selectedInd < 1}
        >
          <ChevronLeft />
        </button>
        <button
          className={styles.buttonNext}
          type="button"
          onClick={handleNextClick}
          data-testid="standard:gallery:navigation:right"
          disabled={selectedInd >= mediaItems.length - 1}
        >
          <ChevronRight />
        </button>
        {children}
      </div>

      {mediaItems.length > 1 && (
        <Thumbnails
          media={mediaItems}
          ref={thumbsContainerRef}
          scrollToIndex={scrollToIndex}
          selectedInd={selectedInd}
          className={styles.thumbnails}
          fullscreen={fullscreen}
        />
      )}
    </div>
  );
};
